import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { dayKeyMtl } from "../lib/dateMtl";
import { useLang } from "../i18n/LanguageContext";
import { lockScroll, unlockScroll } from "../lib/scrollLock";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

// Plages horaires proposées pour le rappel (demande du propriétaire) : bornes
// réglables dans Admin → Paramètres (« Heures de rappel ») ; 7 h–21 h par défaut
export const buildCallbackHours = (start = "07:00", end = "21:00") => {
  const [hs, ms] = String(start).split(":").map(Number);
  const [he, me] = String(end).split(":").map(Number);
  if ([hs, ms, he, me].some(Number.isNaN) || hs * 60 + ms >= he * 60 + me) {
    if (start !== "07:00" || end !== "21:00") return buildCallbackHours();
  }
  const out = [];
  for (let t = hs * 60 + ms; t <= he * 60 + me; t += 30) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    out.push({
      v: `${String(h).padStart(2, "0")}:${m ? "30" : "00"}`,
      fr: m ? `${h} h 30` : `${h} h`,
      en: `${((h + 11) % 12) + 1}:${m ? "30" : "00"} ${h < 12 ? "AM" : "PM"}`,
    });
  }
  return out;
};
export const CALLBACK_HOURS = buildCallbackHours();

// Bouton « Demander un appel » + boîte de dialogue — réutilisé partout où les
// coordonnées apparaissent (pied de page, bulle Messenger, encarts d'estimé,
// accueil...). Envoie POST /api/call-request → courriel immédiat au propriétaire.
export function CallRequestDialog({ className, testid = "call-request", label }) {
  const { t, ts, lang } = useLang();
  const [open, setOpen] = useState(false);

  // Boîte ouverte : gèle le défilement de la page (Lenis + CSS)
  useEffect(() => {
    if (!open) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [open]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", postal_code: "", organization: "", callback_date: "", callback_from: "", callback_to: "", message: "" });
  // Heures de rappel RÉGLABLES (Admin → Paramètres) — bornent les listes De/À
  const [hours, setHours] = useState(CALLBACK_HOURS);
  useEffect(() => {
    api.get("/settings/shop").then((r) => {
      const ch = r.data?.callback_hours;
      if (ch?.start && ch?.end) setHours(buildCallbackHours(ch.start, ch.end));
    }).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/call-request", form);
      setSent(true);
      setForm({ first_name: "", last_name: "", phone: "", postal_code: "", organization: "", callback_date: "", callback_from: "", callback_to: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setSent(false); setOpen(true); }}
        data-testid={`${testid}-open`}
        className={className || "inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300"}
      >
        <Phone className="w-4 h-4" /> {label || t("contact.call_cta")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-gold/30 text-ivory max-w-md" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-gold">{t("contact.call_title")}</DialogTitle>
          </DialogHeader>
          {sent ? (
            <div className="text-center py-6" data-testid={`${testid}-success`}>
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
              <p className="text-ivory/80 text-sm">{t("contact.call_sent")}</p>
              <button onClick={() => setOpen(false)} data-testid={`${testid}-close`}
                className="mt-6 bg-gold text-ink font-bold px-8 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
                OK
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-xs text-ivory/60">{t("contact.call_sub")}</p>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid={`${testid}-first-name`} required value={form.first_name} onChange={set("first_name")}
                  placeholder={ts("contact.first_name") + " *"}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm placeholder:text-ivory/40 focus:border-gold focus:outline-none transition-colors" />
                <input data-testid={`${testid}-last-name`} required value={form.last_name} onChange={set("last_name")}
                  placeholder={ts("contact.last_name") + " *"}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm placeholder:text-ivory/40 focus:border-gold focus:outline-none transition-colors" />
              </div>
              <input data-testid={`${testid}-phone`} type="tel" required minLength={7} value={form.phone} onChange={set("phone")}
                placeholder={ts("contact.call_phone") + " *"}
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm placeholder:text-ivory/40 focus:border-gold focus:outline-none transition-colors" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid={`${testid}-postal`} value={form.postal_code} onChange={set("postal_code")}
                  required minLength={6} maxLength={7} placeholder={ts("contact.call_postal") + " *"}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm placeholder:text-ivory/40 focus:border-gold focus:outline-none transition-colors" />
                <input data-testid={`${testid}-org`} value={form.organization} onChange={set("organization")}
                  placeholder={ts("contact.call_org")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm placeholder:text-ivory/40 focus:border-gold focus:outline-none transition-colors" />
              </div>
              {/* Agenda de rappel (demande du propriétaire) : date + plage horaire
                  souhaitées — crée un événement « Rappel » dans le Google Agenda
                  du propriétaire (quand il est connecté dans Admin → Paramètres) */}
              <div>
                <p className="text-xs text-ivory/60 mb-1.5">{ts("contact.call_time")}</p>
                <div className="grid grid-cols-[1fr_5.4rem_5.4rem] gap-2">
                  <input type="date" data-testid={`${testid}-date`} value={form.callback_date}
                    min={dayKeyMtl()} onChange={set("callback_date")}
                    aria-label={ts("contact.call_date")}
                    className="w-full min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-3 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors" />
                  <select data-testid={`${testid}-from`} value={form.callback_from} onChange={set("callback_from")}
                    aria-label={ts("contact.call_from")}
                    className="bg-ink border border-ivory/15 rounded-lg px-2 py-3 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    <option value="">{ts("contact.call_from")}</option>
                    {hours.map((h) => <option key={h.v} value={h.v}>{lang === "en" ? h.en : h.fr}</option>)}
                  </select>
                  <select data-testid={`${testid}-to`} value={form.callback_to} onChange={set("callback_to")}
                    aria-label={ts("contact.call_to")}
                    className="bg-ink border border-ivory/15 rounded-lg px-2 py-3 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    <option value="">{ts("contact.call_to")}</option>
                    {hours.map((h) => <option key={h.v} value={h.v}>{lang === "en" ? h.en : h.fr}</option>)}
                  </select>
                </div>
              </div>
              <textarea data-testid={`${testid}-message`} rows={2} value={form.message} onChange={set("message")}
                placeholder={ts("contact.call_subject")}
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm placeholder:text-ivory/40 focus:border-gold focus:outline-none transition-colors" />
              <button data-testid={`${testid}-submit`} type="submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                {loading ? t("contact.sending") : t("contact.call_send")}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
